import React from 'react';
import Icon from '../../../components/AppIcon';

const RegionalContext = ({ region, language }) => {
  const contextData = {
    en: {
      us: {
        title: "Financial Planning for United States",
        description: "Navigate your financial future with tools designed for American households. Our calculators help you understand your savings runway and debt payoff timeline using USD.",
        insights: [
          "Average American household debt: $145,000 including mortgages",
          "Recommended emergency fund: 3-6 months of expenses",
          "Credit card debt average: $6,194 per household",
          "Median household income: $74,580 annually"
        ],
        currency: "USD ($)",
        dateFormat: "MM/DD/YYYY"
      },
      uk: {
        title: "Financial Planning for United Kingdom",
        description: "Plan your financial journey with calculators tailored for UK residents. Calculate how long your savings will last and when you\'ll be debt-free using GBP.",
        insights: [
          "Average UK household debt: £63,000 including mortgages",
          "Recommended emergency fund: 3-6 months of expenses",
          "Average credit card debt: £2,100 per household",
          "Median household income: £31,400 annually"
        ],
        currency: "GBP (£)",
        dateFormat: "DD/MM/YYYY"
      }
    },
    es: {
      es: {
        title: "Planificación Financiera para España",
        description: "Navega tu futuro financiero con herramientas diseñadas para hogares españoles. Nuestras calculadoras te ayudan a entender tu margen de ahorro y cronograma de pago de deudas usando EUR.",
        insights: [
          "Deuda promedio de hogares españoles: €85,000 incluyendo hipotecas",
          "Fondo de emergencia recomendado: 3-6 meses de gastos",
          "Deuda promedio de tarjetas de crédito: €2,500 por hogar",
          "Ingreso medio del hogar: €29,400 anuales"
        ],
        currency: "EUR (€)",
        dateFormat: "DD/MM/YYYY"
      },
      mx: {
        title: "Planificación Financiera para México",
        description: "Planifica tu camino financiero con calculadoras adaptadas para residentes mexicanos. Calcula cuánto durarán tus ahorros y cuándo estarás libre de deudas usando MXN.",
        insights: [
          "Deuda promedio de hogares mexicanos: $450,000 MXN incluyendo hipotecas",
          "Fondo de emergencia recomendado: 3-6 meses de gastos",
          "Deuda promedio de tarjetas de crédito: $35,000 MXN por hogar",
          "Ingreso medio del hogar: $180,000 MXN anuales"
        ],
        currency: "MXN ($)",
        dateFormat: "DD/MM/YYYY"
      }
    },
    pt: {
      br: {
        title: "Planejamento Financeiro para Brasil",
        description: "Navegue seu futuro financeiro com ferramentas projetadas para famílias brasileiras. Nossas calculadoras ajudam você a entender sua margem de economia e cronograma de pagamento de dívidas usando BRL.",
        insights: [
          "Dívida média das famílias brasileiras: R$ 85.000 incluindo hipotecas",
          "Fundo de emergência recomendado: 3-6 meses de despesas",
          "Dívida média de cartão de crédito: R$ 4.500 por família",
          "Renda média familiar: R$ 28.000 anuais"
        ],
        currency: "BRL (R$)",
        dateFormat: "DD/MM/YYYY"
      },
      pt: {
        title: "Planeamento Financeiro para Portugal",
        description: "Planeie o seu caminho financeiro com calculadoras adaptadas para residentes portugueses. Calcule quanto tempo durarão as suas poupanças e quando estará livre de dívidas usando EUR.",
        insights: [
          "Dívida média das famílias portuguesas: €75.000 incluindo hipotecas",
          "Fundo de emergência recomendado: 3-6 meses de despesas",
          "Dívida média de cartão de crédito: €2.200 por família",
          "Rendimento médio familiar: €21.000 anuais"
        ],
        currency: "EUR (€)",
        dateFormat: "DD/MM/YYYY"
      }
    },
    fr: {
      fr: {
        title: "Planification Financière pour France",
        description: "Naviguez votre avenir financier avec des outils conçus pour les ménages français. Nos calculatrices vous aident à comprendre votre marge d\'épargne et votre calendrier de remboursement de dettes en EUR.",
        insights: [
          "Dette moyenne des ménages français: 85 000 € y compris les hypothèques",
          "Fonds d\'urgence recommandé: 3-6 mois de dépenses",
          "Dette moyenne de carte de crédit: 2 800 € par ménage",
          "Revenu médian des ménages: 31 000 € par an"
        ],
        currency: "EUR (€)",
        dateFormat: "DD/MM/YYYY"
      },
      be: {
        title: "Planification Financière pour Belgique",
        description: "Planifiez votre parcours financier avec des calculatrices adaptées aux résidents belges. Calculez combien de temps dureront vos économies et quand vous serez libre de dettes en EUR.",
        insights: [
          "Dette moyenne des ménages belges: 78 000 € y compris les hypothèques",
          "Fonds d\'urgence recommandé: 3-6 mois de dépenses",
          "Dette moyenne de carte de crédit: 2 400 € par ménage",
          "Revenu médian des ménages: 29 500 € par an"
        ],
        currency: "EUR (€)",
        dateFormat: "DD/MM/YYYY"
      }
    },
    de: {
      de: {
        title: "Finanzplanung für Deutschland",
        description: "Navigieren Sie Ihre finanzielle Zukunft mit Tools, die für deutsche Haushalte entwickelt wurden. Unsere Rechner helfen Ihnen, Ihre Sparreserve und Ihren Schuldenabbau-Zeitplan in EUR zu verstehen.",
        insights: [
          "Durchschnittliche Verschuldung deutscher Haushalte: 85.000 € einschließlich Hypotheken",
          "Empfohlener Notfallfonds: 3-6 Monate Ausgaben",
          "Durchschnittliche Kreditkartenschulden: 2.600 € pro Haushalt",
          "Medianes Haushaltseinkommen: 47.700 € jährlich"
        ],
        currency: "EUR (€)",
        dateFormat: "DD.MM.YYYY"
      },
      at: {
        title: "Finanzplanung für Österreich",
        description: "Planen Sie Ihren finanziellen Weg mit Rechnern, die für österreichische Einwohner angepasst sind. Berechnen Sie, wie lange Ihre Ersparnisse reichen und wann Sie schuldenfrei sein werden in EUR.",
        insights: [
          "Durchschnittliche Verschuldung österreichischer Haushalte: 72.000 € einschließlich Hypotheken",
          "Empfohlener Notfallfonds: 3-6 Monate Ausgaben",
          "Durchschnittliche Kreditkartenschulden: 2.300 € pro Haushalt",
          "Medianes Haushaltseinkommen: 43.200 € jährlich"
        ],
        currency: "EUR (€)",
        dateFormat: "DD.MM.YYYY"
      }
    }
  };

  const data = contextData?.[language]?.[region] || contextData?.en?.us;

  return (
    <div className="bg-card border border-border rounded-lg p-6 md:p-8 mb-8">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="MapPin" size={24} color="var(--color-accent)" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
            {data?.title}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {data?.description}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-md">
          <Icon name="DollarSign" size={20} color="var(--color-primary)" />
          <div>
            <div className="text-xs text-muted-foreground">Currency</div>
            <div className="text-sm font-medium text-foreground">{data?.currency}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-md">
          <Icon name="Calendar" size={20} color="var(--color-primary)" />
          <div>
            <div className="text-xs text-muted-foreground">Date Format</div>
            <div className="text-sm font-medium text-foreground">{data?.dateFormat}</div>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="TrendingUp" size={18} color="var(--color-success)" />
          Regional Financial Insights
        </h3>
        <ul className="space-y-2">
          {data?.insights?.map((insight, index) => (
            <li key={index} className="flex items-start gap-2">
              <Icon 
                name="Info" 
                size={16} 
                color="var(--color-accent)" 
                className="flex-shrink-0 mt-0.5"
              />
              <span className="text-sm text-foreground">{insight}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default RegionalContext;